/** Section 12 - what a special building actually does.
 *
 * Every town building already carries a `modifiers` list, and until now the
 * town read only two families out of it: `income.*` and `growth*`. Everything
 * else - the Stables' four hundred movement points, the Lighthouse's sea
 * legs, the Hall of Heroes's point of Attack, the Tavern's morale - was
 * authored, validated, shipped, drawn on the town screen, and read by nothing.
 *
 * The missing half is not *what* the number is but *who it lands on*, and the
 * answer is already in the modifier: **how long it lasts says where it goes.**
 *
 *   stat is income/growth   the town itself, whatever the expiry says
 *   no expiry               the kingdom: every hero this player owns, always
 *   expires "turns"         the hero who walks in, for that many days
 *   expires "permanent"     the hero who walks in, once and forever
 *   expires "battle"        this town's own defence, in a battle fought here
 *
 * That is one rule with no table of building ids behind it, and it is why a
 * faction can be given a new special building without a line of code. The cost
 * is that an author has to mean the expiry they write; `data-authoring.spec.ts`
 * pins the four scopes against the shipped data so a slip is a failing test.
 */

import type { Modifier, StatKey, CreatureModifiers } from "../modifiers.js";
import type { PlayerId, TownId } from "../ids.js";
import { asModifierSrcId } from "../ids.js";
import type { BuildingDef } from "../content/registry.js";
import type { HeroState, TownState } from "../state.js";
import { entityKeys } from "../util/iter.js";
import type { Ctx } from "./ctx.js";
import { patchHero } from "./ctx.js";
import { payNum, payStr, payloadOf } from "./payload.js";

export type BuildingScope = "town" | "kingdom" | "visit" | "grant" | "defence";

/**
 * The defs a town has built, in id order (I3).
 *
 * It lives here rather than in `town.ts` because `hero.ts` needs the kingdom's
 * building modifiers and `town.ts` needs the visit rules, and a module that
 * imported both would close a cycle: under ESM that is not a lint complaint but
 * a `ReferenceError` the first time a battle starts. `buildings.ts` therefore
 * imports nothing from the rest of the subsystem, and `town.ts` re-exports this
 * so its own callers do not have to know it moved.
 *
 * `t.built` is *stored* sorted - `createTown`, `addBuilding` and `demoteCapitol`
 * are its only writers and each sorts before it patches - so this walks it as it
 * lies rather than copying and re-sorting on every read.
 * `town-buildings.spec.ts` pins that invariant against the three writers.
 */
export function builtDefs(c: Ctx, t: TownState): BuildingDef[] {
  const out: BuildingDef[] = [];
  for (const id of t.built) {
    const b = c.content.buildings.get(id);
    if (b !== undefined) out.push(b);
  }
  return out;
}

/**
 * Stats that belong to the town's ledger rather than to anybody standing in it.
 *
 * `guildSpells` is named rather than matched by prefix: the Tower Library
 * widens the guild it stands in and expires never, so the expiry rule alone
 * would post it to the kingdom and hand every hero a number no hero reads.
 */
const TOWN_STATS = (stat: string): boolean =>
  stat.startsWith("income.") || stat === "growth" || stat.startsWith("growth.")
  || stat === "guildSpells";

export function scopeOf(m: Modifier): BuildingScope {
  if (TOWN_STATS(m.stat as string)) return "town";
  switch (m.expires?.kind) {
    case undefined: return "kingdom";
    case "permanent": return "grant";
    case "battle": case "round": return "defence";
    case "turns": return "visit";
  }
}

function modifiersInScope(c: Ctx, t: TownState, want: BuildingScope): readonly Modifier[] {
  const out: Modifier[] = [];
  for (const b of builtDefs(c, t)) {
    for (const m of b.modifiers) if (scopeOf(m) === want) out.push(m);
  }
  return out;
}

/**
 * Everything the player's built towns give every hero he owns.
 *
 * `statFor` reaches this, so every movement-legality query pays for it and the
 * scan underneath is over every town *and every object on the map*. It is
 * therefore memoised on the `Ctx`, which is the only cache that can be trusted:
 * a `Ctx` belongs to one command, and the four writers that can move a town or
 * an object empty the memo as they write, so the town captured halfway through a
 * command is already gone from it before the next hero asks.
 *
 * The array is shared with every caller in the command and must not be mutated.
 */
/**
 * Creature bonuses every town this player owns is granting.
 *
 * Kingdom-wide, like `kingdomModifiers`, and for the same reason: the expansion's
 * Guiding Star buffs the Saltreach's creatures wherever they are fighting, not only in
 * the town that built it. Merged into `BattleHeroInput.creatureModifiers`
 * beside the hero's own specialty, so `startBattle` cannot tell the two apart
 * and needs no second code path.
 *
 * Returned unsorted-by-town but stable: `entityKeys` sorts the towns and
 * `builtDefs` returns a town's buildings in `built` order, which is itself
 * sorted, so the same kingdom always produces the same list.
 */
export function kingdomCreatureBonuses(
  c: Ctx, owner: PlayerId,
): readonly CreatureModifiers[] {
  const out: CreatureModifiers[] = [];
  for (const id of entityKeys(c.s.towns)) {
    const t = c.s.towns[id as TownId]!;
    if (t.owner !== owner) continue;
    for (const b of builtDefs(c, t)) {
      for (const cb of b.creatureBonuses) out.push(cb);
    }
  }
  return out;
}

export function kingdomModifiers(c: Ctx, owner: PlayerId): readonly Modifier[] {
  const memo = c.derived.kingdom.get(owner);
  if (memo !== undefined) return memo;

  const out: Modifier[] = [];
  for (const id of entityKeys(c.s.towns)) {
    const t = c.s.towns[id]!;
    if (t.owner !== owner) continue;
    for (const m of modifiersInScope(c, t, "kingdom")) out.push(m);
  }
  for (const m of flaggedObjectModifiers(c, owner)) out.push(m);
  c.derived.kingdom.set(owner, out);
  return out;
}

/**
 * The other half of the kingdom: what a captured *object* is worth to every
 * hero its owner has.
 *
 * A Lighthouse is the case this exists for. It is authored as a `mine` so that
 * flagging it works through machinery that already exists, and what it actually
 * gives is sea legs to the whole kingdom rather than a resource a day. The
 * payload names the stat: `flagStat` is a `StatKey` and `flagValue` is added to
 * it, `stackRule: "sum"` so two lighthouses are worth two.
 *
 * It is here rather than beside the object code because `heroModifiers` has to
 * reach it and the object modules reach back to `hero.ts` - the same cycle that
 * `builtDefs` is in this file to avoid. `payload.ts` is a leaf, so reading a
 * payload from here costs nothing.
 */
export function flaggedObjectModifiers(c: Ctx, owner: PlayerId): readonly Modifier[] {
  const out: Modifier[] = [];
  for (const id of entityKeys(c.s.objects)) {
    const o = c.s.objects[id]!;
    if (o.removed || o.owner !== owner) continue;
    const pay = payloadOf(c, o);
    const stat = payStr(pay, "flagStat");
    const value = payNum(pay, "flagValue", 0);
    if (stat === "" || value === 0) continue;
    out.push({
      source: asModifierSrcId(`object.${o.def}`),
      stat: stat as StatKey, op: "add", value,
      stackGroup: `object.${o.def}`, stackRule: "sum",
    });
  }
  return out;
}

/** What this town's works are worth to the side defending it. */
export function defenceModifiers(c: Ctx, t: TownState): readonly Modifier[] {
  return modifiersInScope(c, t, "defence");
}

/**
 * Apply a town's blessings to the hero who has just walked in.
 *
 * Both kinds are self-tracking through the modifier's own source, so no new
 * field on `HeroState` records who has drunk from which well:
 *
 *  - a **grant** is permanent, and a hero who already carries one from that
 *    source has already had it. Walking in and out of the Hall of Heroes
 *    does not make a hero stronger every lap.
 *  - a **visit** bonus is refreshed: the old copy goes and a new one lands with
 *    its full duration, which is what makes the Stables worth returning to.
 */
export function applyTownVisit(c: Ctx, h: HeroState, t: TownState): void {
  const grants = modifiersInScope(c, t, "grant");
  const visits = modifiersInScope(c, t, "visit");
  if (grants.length === 0 && visits.length === 0) return;

  const held = c.s.heroes[h.id]?.modifiers ?? [];
  const refreshed = new Set<string>(visits.map((m) => m.source as string));
  const next: Modifier[] = held.filter((m) => !refreshed.has(m.source as string));
  const have = new Set<string>(next.map((m) => m.source as string));

  let changed = next.length !== held.length;
  for (const m of grants) {
    if (have.has(m.source as string)) continue;
    next.push(m);
    have.add(m.source as string);
    changed = true;
  }
  for (const m of visits) { next.push(m); changed = true; }
  if (!changed) return;
  patchHero(c, h.id, { modifiers: next });
}
